import { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Check, X } from "lucide-react";
import { toast } from "sonner";

interface Ajuste {
  id: number; idFuncionario: number; funcionarioNome: string; dtReferencia: string;
  iTipoAjuste: number; fMinutosAjuste: number; sJustificativa: string; tipoTexto: string;
}
interface Funcionario { id: number; nome: string; }

const TIPOS = [
  { v: 1, t: "Inclusão de batida" }, { v: 2, t: "Abono de falta" }, { v: 3, t: "Atestado" },
  { v: 4, t: "Ajuste banco de horas" }, { v: 5, t: "Desconsiderar batida" },
];
const hoje = new Date().toISOString().slice(0, 10);
const VAZIO = { IdFuncionario: 0, DtReferencia: hoje, ITipoAjuste: 3, FMinutosAjuste: 0, SJustificativa: "" };

export default function AjustesPage() {
  const [itens, setItens] = useState<Ajuste[]>([]);
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<any>(VAZIO);

  const carregar = () => {
    setCarregando(true);
    apiClient.get<Ajuste[]>("/api/rh/ponto/ajustes/pendentes")
      .then((r) => { setItens(r.data); setSel(new Set()); })
      .catch((e) => toast.error(e.message ?? "Falha ao carregar."))
      .finally(() => setCarregando(false));
  };
  useEffect(() => {
    carregar();
    apiClient.get<Funcionario[]>("/api/rh/ponto/funcionarios", { params: { somenteAtivos: true } })
      .then((r) => setFuncionarios(r.data)).catch(() => {});
  }, []);

  const toggle = (id: number) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const todos = () => setSel((s) => (s.size === itens.length ? new Set() : new Set(itens.map((a) => a.id))));

  const decidir = async (aprovado: boolean) => {
    if (sel.size === 0) { toast.warning("Selecione ao menos um ajuste."); return; }
    try {
      const r = await apiClient.post("/api/rh/ponto/ajustes/aprovar", { IdsAjuste: [...sel], Aprovado: aprovado });
      toast.success(r.data?.mensagem ?? "Processado."); carregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao processar."); }
  };

  const salvarNovo = async () => {
    if (!form.IdFuncionario) { toast.warning("Selecione o funcionário."); return; }
    try {
      await apiClient.post("/api/rh/ponto/ajustes", form);
      toast.success("Ajuste enviado para aprovação."); setDialog(false); setForm(VAZIO); carregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao salvar."); }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Aprovação de Ajustes</h1>
          <p className="text-sm text-muted-foreground">Justificativas pendentes (abono, atestado, inclusão de batida, banco de horas).</p>
        </div>
        <div className="flex gap-2">
          {sel.size > 0 && (
            <>
              <Button variant="outline" onClick={() => decidir(false)}><X className="h-4 w-4 mr-2" />Rejeitar ({sel.size})</Button>
              <Button onClick={() => decidir(true)}><Check className="h-4 w-4 mr-2" />Aprovar ({sel.size})</Button>
            </>
          )}
          <Button variant="secondary" onClick={() => { setForm(VAZIO); setDialog(true); }}><Plus className="h-4 w-4 mr-2" />Novo</Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"><Checkbox checked={itens.length > 0 && sel.size === itens.length} onCheckedChange={todos} /></TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Funcionário</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Minutos</TableHead>
              <TableHead>Justificativa</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {carregando ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nenhuma justificativa pendente.</TableCell></TableRow>
            ) : itens.map((a) => (
              <TableRow key={a.id}>
                <TableCell><Checkbox checked={sel.has(a.id)} onCheckedChange={() => toggle(a.id)} /></TableCell>
                <TableCell className="whitespace-nowrap">{new Date(a.dtReferencia).toLocaleDateString("pt-BR")}</TableCell>
                <TableCell>{a.funcionarioNome || `#${a.idFuncionario}`}</TableCell>
                <TableCell><Badge variant="secondary">{a.tipoTexto}</Badge></TableCell>
                <TableCell>{a.fMinutosAjuste !== 0 ? a.fMinutosAjuste : "—"}</TableCell>
                <TableCell className="max-w-xs truncate">{a.sJustificativa || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo ajuste</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1 col-span-2">
              <Label>Funcionário</Label>
              <Select value={form.IdFuncionario ? String(form.IdFuncionario) : ""} onValueChange={(v) => setForm({ ...form, IdFuncionario: Number(v) })}>
                <SelectTrigger><SelectValue placeholder="Selecione…" /></SelectTrigger>
                <SelectContent>{funcionarios.map((f) => <SelectItem key={f.id} value={String(f.id)}>{f.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Data</Label>
              <Input type="date" value={form.DtReferencia} onChange={(e) => setForm({ ...form, DtReferencia: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select value={String(form.ITipoAjuste)} onValueChange={(v) => setForm({ ...form, ITipoAjuste: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map((t) => <SelectItem key={t.v} value={String(t.v)}>{t.t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Minutos (banco de horas; ± sinal)</Label>
              <Input type="number" value={form.FMinutosAjuste} onChange={(e) => setForm({ ...form, FMinutosAjuste: Number(e.target.value) || 0 })} />
            </div>
            <div className="space-y-1 col-span-2">
              <Label>Justificativa</Label>
              <Input value={form.SJustificativa} onChange={(e) => setForm({ ...form, SJustificativa: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={salvarNovo}>Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
